import { useMemo } from 'react';
import {
  usePosReportSummary,
  usePosTotalDailySales,
  usePosTopItems,
  usePosTotalDailyDepartmentSales,
  usePosTotalDailyCategorySales,
  usePosPromotionReport,
  usePosNewVsReturningCustomers,
} from '@/hooks/use-pos-reports';
import { useEffectiveReportHidden } from '@/hooks/use-report-visibility';
import { useProductsPosConfig } from '@/hooks/use-pos-config';
import { useReportingTimeZone } from '@/hooks/use-reporting-timezone';
import { civilFromInstant } from '@/components/ui/date-picker/date-utils';

/** Wall-clock `YYYY-MM-DDTHH:mm` strings, interpreted in the reporting zone by
 *  withLocalDateRange — the same contract the reports page uses, so the home
 *  page and the reports agree on which business day a sale belongs to. */
export interface HomeRange {
  dateFrom: string;
  dateTo: string;
}

/**
 * A range that starts and ends on the same business day is shown by the hour;
 * anything longer is shown by date. Compared on the wall-clock date part only,
 * since a single-day range still spans 00:00 → 23:59.
 */
const pad = (n: number) => String(n).padStart(2, '0');

export function granularityFor(range: HomeRange): 'day' | 'hour' {
  return range.dateFrom.slice(0, 10) === range.dateTo.slice(0, 10) ? 'hour' : 'day';
}

const DAY = 86_400_000;
const parseDay = (value: string) => Date.parse(`${value.slice(0, 10)}T00:00:00Z`);
const isoDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** Inclusive number of calendar days in a range, on the date part only. */
export function spanDays(range: HomeRange): number {
  const start = parseDay(range.dateFrom);
  const end = parseDay(range.dateTo);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return 1;
  return Math.round((end - start) / DAY) + 1;
}

/**
 * How the Sales Trends card buckets its points. The server only ever returns
 * hours or days ({@link granularityFor}); anything coarser is rolled up from
 * the daily rows on the client, so no report endpoint changes shape.
 *
 * The cut-offs follow the picker's presets: a single day plots by the hour,
 * a week (or "Last 7 Days", or a fortnight) by the day, a month or "Last 30
 * Days" by the week, and a quarter or year by the month.
 */
export type TrendGranularity = 'hour' | 'day' | 'week' | 'month';

export function trendGranularityFor(range: HomeRange): TrendGranularity {
  const days = spanDays(range);
  if (days <= 1) return 'hour';
  if (days <= 14) return 'day';
  if (days <= 63) return 'week';
  return 'month';
}

// A type alias rather than an interface so it stays assignable to the
// `Record<string, number | string>` rows the KPI sparklines take.
export type TrendPoint = {
  /** Bucket key: `YYYY-MM-DDTHH:00` (hour), `YYYY-MM-DD` (day),
   *  `YYYY-MM-DD/YYYY-MM-DD` (week, first/last day inside the range) or
   *  `YYYY-MM` (month). The chart's label formatter tells them apart by shape. */
  date: string;
  sales: number;
  transactions: number;
  refunds: number;
  avgBasket: number;
};

/**
 * "2026-08-24" → "Mon, Aug 24", the hourly bucket "2026-08-24T09:00" → "9 AM",
 * the weekly bucket "2026-08-23/2026-08-29" → "Aug 23" (tooltip: "Aug 23 – Aug 29")
 * and the monthly bucket "2026-08" → "Aug" (tooltip: "August 2026"). The bucket
 * shape comes from `bucketTrend` in use-home-analytics; `long` picks the fuller
 * tooltip wording where the axis tick has to stay short.
 *
 * These are already business-local dates: the server buckets each day with
 * `AT TIME ZONE <reporting zone>` and returns a bare `YYYY-MM-DD`, so the
 * string *is* the day in the timezone shown on this page. Rendering it fixed to
 * UTC is what preserves that — it prints the given calendar date verbatim
 * instead of re-interpreting it.
 *
 * Both other options are wrong. Formatting in the reporting zone would take the
 * implied UTC midnight and shift it backwards, and letting `new Date()` fall
 * back to the browser zone does the same for anyone west of Greenwich: a series
 * point for Aug 24 renders as "Sun, Aug 23", so the axis silently disagrees
 * with the report the card links to.
 */
export function formatTrendBucket(value: string, long = false): string {
  const utc = (opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('en-US', { ...opts, timeZone: 'UTC' });
  const civil = (day: string) => {
    const [y, m, d] = day.split('-').map(Number);
    return y && m && d ? new Date(Date.UTC(y, m - 1, d)) : null;
  };
  if (value.includes('/')) {
    const [from, to] = value.split('/').map(civil);
    if (!from || !to) return value;
    const md = utc({ month: 'short', day: 'numeric' });
    return long ? `${md.format(from)} – ${md.format(to)}` : md.format(from);
  }
  if (value.length === 7) {
    const [y, m] = value.split('-').map(Number);
    if (!y || !m) return value;
    const month = new Date(Date.UTC(y, m - 1, 1));
    return long ? utc({ month: 'long', year: 'numeric' }).format(month) : utc({ month: 'short' }).format(month);
  }
  const [datePart, timePart] = value.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return value;
  if (timePart) {
    const hour = Number(timePart.slice(0, 2));
    if (!Number.isFinite(hour)) return value;
    // One point per hour only ever spans a single day, so the hour alone is
    // label enough — the date is already on the picker above the card.
    return new Intl.DateTimeFormat('en-US', { hour: 'numeric', hour12: true, timeZone: 'UTC' }).format(
      new Date(Date.UTC(y, m - 1, d, hour)),
    );
  }
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

/**
 * Roll points up into day, week or month buckets covering the whole range.
 * Every bucket in the range is emitted, empty ones as zeroes, since the axis
 * is categorical and a missing quiet week would otherwise squeeze its
 * neighbours together and hide the dip. Hourly points pass through: the
 * server already buckets those, and {@link fillHours} does their zero-fill.
 *
 * Weeks start on Sunday, matching the picker's "This Week" preset, and the
 * first and last buckets are clipped to the range so "This Month" opens on
 * the 1st rather than the preceding Sunday. Every numeric field is summed, so
 * callers can carry extra columns (tax, fees, …) through the rollup exactly;
 * the average basket is a ratio and is recomputed from the bucket totals.
 */
export function bucketTrend<T extends TrendPoint>(points: T[], granularity: TrendGranularity, range: HomeRange): T[] {
  if (granularity === 'hour') return points;
  const start = parseDay(range.dateFrom);
  const end = parseDay(range.dateTo);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return points;

  const buckets: Array<{ key: string; from: number; to: number }> = [];
  if (granularity === 'day') {
    for (let from = start; from <= end; from += DAY) buckets.push({ key: isoDay(from), from, to: from });
  } else if (granularity === 'week') {
    let from = start;
    while (from <= end) {
      const saturday = from + (6 - new Date(from).getUTCDay()) * DAY;
      const to = Math.min(saturday, end);
      buckets.push({ key: `${isoDay(from)}/${isoDay(to)}`, from, to });
      from = to + DAY;
    }
  } else {
    const first = new Date(start);
    let cursor = Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), 1);
    while (cursor <= end) {
      const d = new Date(cursor);
      const y = d.getUTCFullYear();
      const m = d.getUTCMonth();
      buckets.push({
        key: `${y}-${pad(m + 1)}`,
        from: Math.max(cursor, start),
        to: Math.min(Date.UTC(y, m + 1, 0), end),
      });
      cursor = Date.UTC(y, m + 1, 1);
    }
  }

  const rolled = buckets.map((b) => zeroPoint(points, b.key));
  const keys = numericKeys(points);
  for (const pt of points) {
    const ms = parseDay(pt.date);
    const i = buckets.findIndex((b) => ms >= b.from && ms <= b.to);
    if (i < 0) continue;
    const acc = rolled[i] as Record<string, unknown>;
    const src = pt as Record<string, unknown>;
    for (const k of keys) acc[k] = (acc[k] as number) + (src[k] as number);
  }
  for (const r of rolled) r.avgBasket = r.transactions > 0 ? r.sales / r.transactions : 0;
  return rolled;
}

/** Move a bucket key (`YYYY-MM-DD` or `YYYY-MM-DDTHH:00`) forward by `days`,
 *  keeping any time part. Used to lay a previous period over the current one. */
export function shiftDays(key: string, days: number): string {
  const ms = parseDay(key);
  if (Number.isNaN(ms)) return key;
  return isoDay(ms + days * DAY) + key.slice(10);
}

/** The hour to fill through: the current hour for today (so the rest of the
 *  day doesn't plot as a crash), 23 for a day that is over. */
export function lastHourOf(day: string, timeZone: string): number {
  const now = civilFromInstant(new Date(), timeZone);
  const isToday = day === `${now.year}-${pad(now.month)}-${pad(now.day)}`;
  return isToday ? now.hour : 23;
}

/**
 * Zero-fill the hours of `day` up to `lastHour`. An hour with no sales comes
 * back as no row at all; plotted as-is the axis is categorical, so a quiet
 * morning would put 8am and 2pm one tick apart and the line would read as a
 * steady climb instead of a dead spell.
 */
export function fillHours<T extends TrendPoint>(points: T[], day: string, lastHour: number): T[] {
  const byHour = new Map(points.map((pt) => [pt.date, pt]));
  const filled: T[] = [];
  for (let h = 0; h <= lastHour; h++) {
    const key = `${day}T${pad(h)}:00`;
    filled.push(byHour.get(key) ?? zeroPoint(points, key));
  }
  return filled;
}

/** Every summable field of a point: all numbers except the avgBasket ratio. */
function numericKeys<T extends TrendPoint>(points: T[]): string[] {
  const sample: Record<string, unknown> = points[0] ?? { sales: 0, transactions: 0, refunds: 0 };
  return Object.keys(sample).filter((k) => k !== 'avgBasket' && typeof sample[k] === 'number');
}

/** A point of the same shape as `points` with every number zeroed. */
function zeroPoint<T extends TrendPoint>(points: T[], date: string): T {
  const zero: Record<string, unknown> = {
    ...(points[0] ?? {}),
    date,
    sales: 0,
    transactions: 0,
    refunds: 0,
    avgBasket: 0,
  };
  for (const k of Object.keys(zero)) if (typeof zero[k] === 'number') zero[k] = 0;
  return zero as T;
}

/**
 * Report-visibility ids behind each home card. Every reporting endpoint sits
 * behind `requireReportVisible(...)`, so a card whose report is hidden for this
 * user at this location must not render *and* must not fire its query — an
 * ungated request comes back 403 and would surface as a broken card on the
 * user's own home page.
 */
export const HOME_REPORT_IDS = {
  summary: 'sales-summary-current-location',
  dailySales: 'total-daily-sales',
  topItems: 'top-items-by-sales-volume',
  departments: 'total-daily-department-sales',
  categories: 'total-daily-category-sales',
  promotions: 'promotion-usage',
  // The customer mix has no report of its own; its endpoint is gated on the
  // customers report whose visibility row already governs customer data.
  customerMix: 'retail-customers',
} as const;

/**
 * The range immediately preceding `range`, of equal length, used for the
 * "vs last period" deltas. Anchored on the wall-clock date strings rather than
 * instants so a DST boundary inside the range can't shift the comparison by an
 * hour and silently move a day in or out of it.
 */
export function previousRange(range: HomeRange): HomeRange {
  const start = Date.parse(`${range.dateFrom.slice(0, 10)}T00:00:00Z`);
  const end = Date.parse(`${range.dateTo.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return range;
  // Inclusive span: Sun–Sat is 7 days, so the previous period ends the day
  // before this one starts and reaches back the same number of days.
  const days = spanDays(range);
  return {
    dateFrom: `${isoDay(start - days * DAY)}T00:00`,
    dateTo: `${isoDay(start - DAY)}T23:59`,
  };
}

export interface Delta {
  /** Fractional change vs the previous period; null when there's no baseline
   *  to compare against, which renders as "no prior data" rather than 0%. */
  pct: number | null;
  previous: number;
}

export function toDelta(current: number, previous: number): Delta {
  if (!Number.isFinite(previous) || previous === 0) return { pct: null, previous: previous || 0 };
  return { pct: (current - previous) / Math.abs(previous), previous };
}

const TOP_ITEMS_SHOWN = 5;

/**
 * Every query the home page needs, scoped to the active location and the
 * reporting zone, and gated by report visibility.
 *
 * The summary is fetched twice — once for the range, once for the preceding one
 * — because no endpoint returns a period comparison. That is the whole cost of
 * the "vs last period" deltas: one extra request, not one per card.
 *
 * `enabled` is the caller's role gate: passing false keeps every query off for
 * users who will never be shown the cards, rather than fetching data the page
 * then throws away.
 */
export function useHomeAnalytics(range: HomeRange, locationId: string | null, enabled = true) {
  const { timeZone } = useReportingTimeZone();
  const { data: hiddenData, isLoading: visibilityLoading } = useEffectiveReportHidden(locationId);
  const hidden = useMemo(() => new Set(hiddenData?.data?.hiddenReportIds ?? []), [hiddenData]);

  // Until visibility resolves nothing may fire: optimistically querying would
  // 403 for precisely the users the gate exists to protect.
  // Promotions are additionally behind a per-location super-admin toggle —
  // the same gate that hides the Promotions tab and report category — so the
  // card must disappear when the feature is off, not just when the report is
  // hidden.
  const { data: posConfig } = useProductsPosConfig(locationId);
  const promotionsEnabled = posConfig?.promotionsEnabled === true;

  const ready = enabled && !!locationId && !visibilityLoading;
  const can = (id: string) => ready && !hidden.has(id);

  const scope = { locationId: locationId || undefined };
  const prev = useMemo(() => previousRange(range), [range]);

  const summary = usePosReportSummary({ ...scope, ...range }, { enabled: can(HOME_REPORT_IDS.summary) });
  const summaryPrev = usePosReportSummary({ ...scope, ...prev }, { enabled: can(HOME_REPORT_IDS.summary) });
  const granularity = granularityFor(range);
  const dailySales = usePosTotalDailySales(
    { ...scope, ...range, granularity },
    { enabled: can(HOME_REPORT_IDS.dailySales) },
  );
  const topItems = usePosTopItems(
    { ...scope, ...range, page: 1, perPage: TOP_ITEMS_SHOWN },
    { enabled: can(HOME_REPORT_IDS.topItems) },
  );
  const departments = usePosTotalDailyDepartmentSales(
    { ...scope, ...range },
    { enabled: can(HOME_REPORT_IDS.departments) },
  );
  const categories = usePosTotalDailyCategorySales(
    { ...scope, ...range },
    { enabled: can(HOME_REPORT_IDS.categories) },
  );
  // The promotion report already aggregates per promotion and supports
  // sorting, so the top N comes straight off page 1 — no client-side rollup.
  const promotions = usePosPromotionReport(
    {
      ...scope,
      ...range,
      page: 1,
      perPage: TOP_ITEMS_SHOWN,
      sortBy: 'totalUses',
      sortDir: 'desc',
    },
    { enabled: can(HOME_REPORT_IDS.promotions) && promotionsEnabled },
  );

  const customerMix = usePosNewVsReturningCustomers(
    { ...scope, ...range },
    { enabled: can(HOME_REPORT_IDS.customerMix) },
  );

  const s = summary.data?.data;
  const p = summaryPrev.data?.data;

  /** The four headline figures, each paired with its change vs last period. */
  const kpis = useMemo(() => {
    if (!s) return null;
    const netSales = s.sales.net;
    const transactions = s.analytics.totalTransactions;
    const avgBasket = s.analytics.avgPerTransaction;
    const refunds = s.refunds.total;
    return {
      netSales: { value: netSales, delta: toDelta(netSales, p?.sales.net ?? 0) },
      transactions: { value: transactions, delta: toDelta(transactions, p?.analytics.totalTransactions ?? 0) },
      avgBasket: { value: avgBasket, delta: toDelta(avgBasket, p?.analytics.avgPerTransaction ?? 0) },
      refunds: {
        value: refunds,
        delta: toDelta(refunds, p?.refunds.total ?? 0),
        count: s.refunds.fromOrders.count + s.refunds.withoutOrder.count,
      },
    };
  }, [s, p]);

  /** The series driving the sparklines and the trend chart — one point per hour
   *  for a single-day range, per date otherwise. The endpoint returns rows
   *  newest-first (the report table's order), so they are sorted ascending here
   *  or the chart would run backwards along the x-axis. */
  const series = useMemo(() => {
    const rows = [...(dailySales.data?.data?.daily ?? [])].sort((a, b) => a.date.localeCompare(b.date));
    const points: TrendPoint[] = rows.map((r) => ({
      date: r.date,
      sales: r.sales,
      transactions: r.ordersCount,
      refunds: r.refundedAmount,
      // Per-bucket average basket, so that card's sparkline plots the metric it
      // is labelled with rather than raw sales.
      avgBasket: r.ordersCount > 0 ? r.sales / r.ordersCount : 0,
    }));
    if (granularity !== 'hour') return points;
    // Hours come back sparse (see fillHours); fill through the current hour
    // for today and through 23:00 for a day that is over.
    const day = range.dateFrom.slice(0, 10);
    return fillHours(points, day, lastHourOf(day, timeZone));
  }, [dailySales.data, granularity, range.dateFrom, timeZone]);

  /** The Sales Trends card's own view of the series: the hourly or daily
   *  points above, rolled up to weeks or months for longer ranges. The KPI
   *  sparklines keep the finer series so their shape doesn't change. */
  const trendGranularity = trendGranularityFor(range);
  const trendSeries = useMemo(() => bucketTrend(series, trendGranularity, range), [series, trendGranularity, range]);

  const paymentMethods = useMemo(() => {
    const rows = s?.paymentMethods ?? [];
    return [...rows].sort((a, b) => b.total - a.total);
  }, [s]);

  const topByVolume = useMemo(() => topItems.data?.data ?? [], [topItems.data]);

  const promotionRows = useMemo(() => promotions.data?.data ?? [], [promotions.data]);

  const departmentRows = useMemo(
    () => [...(departments.data?.data ?? [])].sort((a, b) => b.grossSale - a.grossSale).slice(0, TOP_ITEMS_SHOWN),
    [departments.data],
  );

  const categoryRows = useMemo(
    () => [...(categories.data?.data ?? [])].sort((a, b) => b.grossSale - a.grossSale).slice(0, TOP_ITEMS_SHOWN),
    [categories.data],
  );

  return {
    ready,
    can,
    granularity,
    trendGranularity,
    promotionsEnabled,
    prev,
    kpis,
    series,
    trendSeries,
    paymentMethods,
    topByVolume,
    promotionRows,
    departmentRows,
    categoryRows,
    customerMix: customerMix.data?.data ?? null,
    breakdown: s
      ? {
          subtotal: s.sales.subtotal,
          discount: s.sales.discount,
          tax: s.sales.tax,
          tips: s.sales.tips,
          net: s.sales.net,
          netAfterRefunds: s.sales.netAfterRefunds,
        }
      : null,
    loading: {
      summary: summary.isLoading || summaryPrev.isLoading,
      series: dailySales.isLoading,
      topItems: topItems.isLoading,
      departments: departments.isLoading,
      categories: categories.isLoading,
      promotions: promotions.isLoading,
      customerMix: customerMix.isLoading,
    },
  };
}
